import axios from "@/utils/axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface Payment {
  id: string;
  payable_type: string;
  payable_id: string;
  provider: string;
  provider_payment_id: string;
  provider_reference: string | null;
  amount: string;
  currency: string;
  status: "PENDING" | "PAID" | "FAILED" | "CANCELLED";
  description: string;
  metadata: Record<string, unknown> | null;
  provider_response: Record<string, unknown> | null;
  expires_at: string | null;
  paid_at: string | null;
  cancelled_at: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Applicant {
  id: string;
  document_number: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  birthdate: string;
  email_verified_at: string | null;
  keycloak_id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  status: number;
  type: string;
  document_type: string;
  commercial_name: string | null;
  category: string;
}

export interface PaymentMedia {
  id: string;
  model_type: string;
  model_id: string;
  name: string;
  file_name: string;
  collection: string;
  size: number;
  url: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaymentRequest {
  id: string;
  node_execution_id: string;
  applicant_id: string;
  payment_method: "CREDIT_CARD" | "NPE" | null;
  status:
    | "PENDING"
    | "PAID"
    | "REJECTED"
    | "EXPIRED"
    | "FAILED"
    | "CANCELLED"
    | "EXEMPT";
  amount: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  is_eximible: boolean | null;
  metadata: Record<string, unknown> | null;
  is_mandatory: boolean;
  paid_difference_amount?: number;
  exempt_reason?: string | null;
  exempted_by?: string | null;
  exempted_at?: string | null;
  exemptedBy?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  applicant?: Applicant;
  payments?: Payment[];
  media?: PaymentMedia;
  node_execution?: {
    flow_execution_id?: string;
  };
}

/**
 * Obtiene los detalles de una solicitud de pago por su ID
 */
const getPaymentRequest = async (
  paymentRequestId: string,
): Promise<PaymentRequest> => {
  const response = await axios.get<{ data: PaymentRequest }>(
    `/admin/v1/payment/requests/${paymentRequestId}`,
    {
      params: {
        relationships:
          "nodeExecution.flowExecution.formalityRequests,payments,applicant,media,exemptedBy",
      },
    },
  );
  return response.data.data;
};

/**
 * Hook para obtener los detalles de una solicitud de pago
 */
export const useGetPaymentRequest = (paymentRequestId: string) => {
  return useQuery({
    queryKey: ["payment-request", paymentRequestId],
    queryFn: async () => {
      return await getPaymentRequest(paymentRequestId);
    },
    enabled: !!paymentRequestId,
  });
};

/**
 * Sincroniza los pagos mediante NPE
 */
const syncNpePayments = async (file: File): Promise<void> => {
  const formData = new FormData();
  formData.append("document", file);

  const response = await axios.post("/admin/v1/payment/sync/npe", formData);
  return response.data;
};

/**
 * Hook para sincronizar pagos NPE
 */
export const useSyncNpePayments = () => {
  return useMutation({
    mutationFn: syncNpePayments,
  });
};

const continuePaymentRequest = async (
  paymentRequestId: string,
): Promise<void> => {
  const response = await axios.post(
    `/admin/v1/payment/requests/${paymentRequestId}`,
  );
  return response.data;
};

export const useContinuePaymentRequest = (flowExecutionId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentRequestId: string) =>
      continuePaymentRequest(paymentRequestId),
    onSuccess: (_data, paymentRequestId) => {
      queryClient.invalidateQueries({
        queryKey: ["payment-request", paymentRequestId],
      });
      if (flowExecutionId) {
        // Invalidar flow execution (incluye node_executions)
        queryClient.invalidateQueries({
          queryKey: ["flow-execution", flowExecutionId],
        });
        // Invalidar también las listas de flow executions
        queryClient.invalidateQueries({
          queryKey: ["flow-executions"],
        });
      }
    },
  });
};

export const downloadNpePdf = async (
  paymentRequestId: string,
  fileName: string = "Mandamiento de ingreso.pdf",
): Promise<void> => {
  const response = await axios.get(
    `/admin/v1/payment/requests/${paymentRequestId}/npe-pdf`,
    { responseType: "blob" },
  );

  const url = globalThis.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};

export const downloadOnlinePaymentPdf = async (
  paymentRequestId: string,
  fileName: string = "mandamiento-tarjeta.pdf",
): Promise<void> => {
  const response = await axios.get(
    `/admin/v1/payment/requests/${paymentRequestId}/online-payment-pdf`,
    { responseType: "blob" },
  );

  const url = globalThis.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};

export const downloadReceiptPdf = async (
  paymentRequestId: string,
  fileName: string = "comprobante-pago.pdf",
): Promise<void> => {
  const response = await axios.get(
    `/admin/v1/payment/requests/${paymentRequestId}/receipt-pdf`,
    { responseType: "blob" },
  );

  const url = globalThis.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};

export interface ExemptPaymentRequestParams {
  payment_request_id: string;
  comments: string;
  attachment?: File | null;
}

/**
 * Exime el pago de una solicitud de pago
 */
const exemptPaymentRequest = async (
  params: ExemptPaymentRequestParams,
): Promise<void> => {
  let response;

  if (params.attachment) {
    // If there's a file, use FormData
    const formData = new FormData();
    formData.append("payment_request_id", params.payment_request_id);
    formData.append("comments", params.comments);
    formData.append("attachment", params.attachment);

    response = await axios.post(
      `/admin/v1/payment/exempt-payment-request`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
  } else {
    // If no file, send JSON
    response = await axios.post(`/admin/v1/payment/exempt-payment-request`, {
      payment_request_id: params.payment_request_id,
      comments: params.comments,
    });
  }

  return response.data;
};

/**
 * Hook para exonerar una solicitud de pago
 */
export const useExemptPaymentRequest = (flowExecutionId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: ExemptPaymentRequestParams) =>
      exemptPaymentRequest(params),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["payment-request", variables.payment_request_id],
      });
      if (flowExecutionId) {
        // Invalidar flow execution (incluye node_executions)
        queryClient.invalidateQueries({
          queryKey: ["flow-execution", flowExecutionId],
        });
        // Invalidar también las listas de flow executions
        queryClient.invalidateQueries({
          queryKey: ["flow-executions"],
        });
      }
    },
  });
};
